import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

const COPY = {
  en: {
    checking: "Confirming your payment...",
    success: "You're now DIY Legal Premium!",
    subtitle: "Thanks for supporting the platform. Your Premium features are unlocked.",
    cta_summarize: "Try the document explainer",
    cta_practice: "Practice talking in court",
    timeout: "Still processing. This can take up to a minute. Refresh this page shortly.",
  },
  es: {
    checking: "Confirmando tu pago...",
    success: "¡Ya eres DIY Legal Premium!",
    subtitle: "Gracias por apoyar la plataforma. Tus funciones Premium están desbloqueadas.",
    cta_summarize: "Prueba el explicador de documentos",
    cta_practice: "Practica hablar en la corte",
    timeout: "Aún procesando. Puede tomar hasta un minuto. Actualiza la página en un momento.",
  },
};

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const { refreshMe } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [status, setStatus] = useState("checking"); // checking | paid | timeout
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("timeout"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (refreshMe) await refreshMe();
          return;
        }
      } catch {}
      setAttempts((a) => a + 1);
    };
    poll();
    const iv = setInterval(poll, 2000);
    const timeout = setTimeout(() => { if (!cancelled && status !== "paid") { clearInterval(iv); setStatus("timeout"); } }, 30000);
    return () => { cancelled = true; clearInterval(iv); clearTimeout(timeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto py-10" data-testid="payment-success-page">
      <div className="bg-white border-2 border-[#E4DDD3] rounded-3xl p-8 sm:p-10 text-center shadow-lg">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#C85A32] animate-spin" />
            <p className="mt-4 text-lg text-[#4A525A]">{c.checking} ({attempts}s)</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500 text-white flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h1 className="mt-5 text-2xl sm:text-3xl font-bold">{c.success}</h1>
            <p className="mt-3 text-[#4A525A]">{c.subtitle}</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/summarize" data-testid="success-goto-summarize" className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> {c.cta_summarize}
              </Link>
              <Link to="/practice" data-testid="success-goto-practice" className="pod-focus border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] font-bold py-3 px-6 rounded-xl">
                {c.cta_practice}
              </Link>
            </div>
          </>
        )}
        {status === "timeout" && (
          <p className="text-[#4A525A]">{c.timeout}</p>
        )}
      </div>
    </div>
  );
}
